<script setup lang="ts">
import { computed, ref, useId } from "vue";
import OKCancelModal from "../../components/OKCancelModal.vue";
import {
  buildBlankShippingPo,
  planProportionalLeftoverMove,
  type EditableShippingPoLine,
  type LeftoverMoveMode,
} from "./shippingPoEditing.ts";

const props = defineProps<{
  lines: EditableShippingPoLine[];
  cubeCapacityFt3: number;
  onConfirm: (mode: LeftoverMoveMode) => void;
  onCancel: () => void;
}>();
const mode = ref<LeftoverMoveMode>("fit");
const controlId = useId();
const plan = computed(() =>
  planProportionalLeftoverMove(
    buildBlankShippingPo("preview", 1, props.cubeCapacityFt3),
    props.lines.map((line) => ({
      ...line,
      requestedUnits: line.qty,
      cubicFeet: line.qty * line.cubicFeetPerUnit,
      shipDate: "",
      shipToWarehouse: "",
      status: "Ready" as const,
    })),
    Object.fromEntries(props.lines.map((line) => [line.sku, line.qty]))
  )
);
const hasFittingQuantity = computed(() =>
  Object.values(plan.value.quantitiesBySku).some((quantity) => quantity > 0)
);
function confirm(): void {
  props.onConfirm(mode.value);
}
</script>

<template>
  <OKCancelModal
    title="Move selected quantities to new PO"
    :ok-button-text="
      mode === 'fit' && (!plan.capacityMeasurable || !hasFittingQuantity)
        ? ''
        : 'Confirm move'
    "
    cancel-button-text="Cancel"
    :on-ok="confirm"
    :on-cancel="onCancel"
  >
    <p>
      Creates one local PO. Any quantity that does not fit stays in the source
      PO.
    </p>
    <fieldset class="new-po-transfer-options">
      <legend>Capacity behavior</legend>
      <label :for="`${controlId}-fit`">
        <input
          :id="`${controlId}-fit`"
          v-model="mode"
          type="radio"
          value="fit"
          :name="controlId"
        />
        Proportional fit within run cube capacity
      </label>
      <label :for="`${controlId}-override`">
        <input
          :id="`${controlId}-override`"
          v-model="mode"
          type="radio"
          value="override"
          :name="controlId"
        />
        Explicit override: move the full request even if capacity is exceeded or
        unavailable
      </label>
    </fieldset>
    <p v-if="!plan.capacityMeasurable" role="alert">
      Capacity cannot be measured. Choose the explicit override to continue.
    </p>
    <p v-else-if="!hasFittingQuantity" role="alert">
      No selected units fit within capacity. Choose the explicit override or
      adjust the selection to continue.
    </p>
    <ul>
      <li v-for="line in lines" :key="line.sku">
        {{ line.sku }}:
        {{
          mode === "override" ? line.qty : (plan.quantitiesBySku[line.sku] ?? 0)
        }}
        of {{ line.qty }} units
      </li>
    </ul>
  </OKCancelModal>
</template>

<style scoped>
.new-po-transfer-options {
  display: grid;
  gap: 0.75rem;
  margin: 0.75rem 0;
  border: 0;
  padding: 0;
}

.new-po-transfer-options legend {
  margin-bottom: 0.5rem;
}

.new-po-transfer-options label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.new-po-transfer-options input {
  flex-shrink: 0;
  margin-top: 0.2rem;
}
</style>
